// screen: Players → App crashes ("קריסות אפליקציה", backend startup/QA brief §3.3, 2026-10-18): first-party crash/ANR
// reports of the TV and mobile apps. Crashed sessions per app version (the staged-rollout gate), top signatures with
// counts and affected models, and the newest scrubbed stack of a signature. Read-only, scope stats:read.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Bug } from 'lucide-react';
import type { AppCrashGroup } from '@viewstream/api-client';
import { Badge, EmptyState, Skeleton } from '@viewstream/ui';
import { fmtDateTime, fmtNumber } from '@viewstream/i18n';
import { api } from '~/app/api';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';

export const Route = createFileRoute('/_layout/t/$slug/players/crashes')({ component: Screen });

const PLATFORMS = ['', 'androidtv', 'android', 'tizen', 'webos', 'ios', 'tvos', 'web'];

function Screen() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  React.useEffect(() => { document.title = appTitle(t`App crashes`); }, [t]);
  const [platform, setPlatform] = React.useState('');
  const [days, setDays] = React.useState(7);
  const [version, setVersion] = React.useState<number | undefined>();
  const [sel, setSel] = React.useState<string | null>(null);
  const query = { platform: platform || undefined, days, version };

  const versions = useQuery({
    queryKey: ['app-crash-versions', tenant.slug, platform, days],
    queryFn: async () => (await api.GET('/v1/apps/crashes/versions', { params: { query: { platform: platform || undefined, days } } })).data!,
  });
  const groups = useQuery({
    queryKey: ['app-crashes', tenant.slug, platform, days, version],
    queryFn: async () => (await api.GET('/v1/apps/crashes', { params: { query } })).data!,
  });
  const n = (v: number) => fmtNumber(v, locale);

  return (
    <div className="space-y-6">
      <Link to={'/t/$slug/players' as never} params={{ slug: tenant.slug } as never} className="mb-2 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="size-4 icon-dir" aria-hidden /> {t`Players`}
      </Link>
      <div>
        <h1 className="text-2xl font-semibold">{t`App crashes`}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t`Crash and freeze (ANR) reports sent by your TV and mobile apps. Each report is one app session that crashed. Addresses, tokens, e-mails and phone numbers are removed before storage; reports are kept 30 days.`}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">{t`Platform`}
          <select className="rounded-md border border-line bg-surface px-2 py-1" value={platform} onChange={(e) => { setPlatform(e.target.value); setVersion(undefined); setSel(null); }} data-testid="crash-platform">
            {PLATFORMS.map((p) => <option key={p} value={p}>{p || t`All`}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">{t`Period`}
          <select className="rounded-md border border-line bg-surface px-2 py-1" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[1, 7, 14, 30].map((d) => <option key={d} value={d}>{t`${d} days`}</option>)}
          </select>
        </label>
        {version !== undefined ? (
          <button type="button" className="text-primary underline-offset-4 hover:underline" onClick={() => setVersion(undefined)}>{t`All versions`}</button>
        ) : null}
      </div>

      <section>
        <h2 className="mb-2 font-semibold">{t`By app version`}</h2>
        {versions.isPending ? <Skeleton className="h-24" /> : !versions.data?.versions.length ? (
          <p className="text-sm text-fg-muted">{t`No crashes reported in this period.`}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-start text-fg-muted"><tr><th className="py-1 text-start">{t`Platform`}</th><th className="text-start">{t`Version code`}</th><th className="text-start">{t`Crashed sessions`}</th><th className="text-start">{t`Signatures`}</th></tr></thead>
            <tbody>
              {versions.data.versions.map((v) => (
                <tr key={v.platform + v.app_version_code} className={`cursor-pointer border-t border-line hover:bg-surface ${version === v.app_version_code ? 'bg-surface' : ''}`} onClick={() => { setVersion(v.app_version_code); setSel(null); }}>
                  <td className="py-1">{v.platform}</td><td><bdi>{v.app_version_code}</bdi></td><td><bdi>{n(v.crashed_sessions)}</bdi></td><td><bdi>{n(v.signatures)}</bdi></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="mb-2 font-semibold">{version !== undefined ? t`Top signatures in version ${version}` : t`Top signatures`}</h2>
        {groups.isPending ? <Skeleton className="h-40" /> : !groups.data?.groups.length ? (
          <EmptyState icon={<Bug className="size-6" aria-hidden />} title={t`No crashes`} description={t`Nothing was reported for this filter.`} />
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {groups.data.groups.map((g) => (
              <li key={g.id}>
                <button type="button" className="w-full p-3 text-start hover:bg-surface" onClick={() => setSel(sel === g.id ? null : g.id)} aria-expanded={sel === g.id}>
                  <GroupLine g={g} n={n} when={fmtDateTime(g.last_seen, { locale, tz })} />
                </button>
                {sel === g.id ? <GroupDetail id={g.id} query={query} n={n} /> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function GroupLine({ g, n, when }: { g: AppCrashGroup; n: (v: number) => string; when: string }) {
  const { t } = useLingui();
  const kind = { java: t`Crash`, js: t`Script error`, anr: t`Freeze (ANR)`, native: t`Native crash` }[g.kind];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Badge tone={g.kind === 'anr' ? 'warn' : 'danger'}>{kind}</Badge>
      <code className="min-w-0 flex-1 truncate text-xs" dir="ltr">{g.signature}</code>
      <span className="text-xs text-fg-muted">{t`${n(g.reports)} sessions · ${n(g.models)} models · top: ${g.top_model || '—'}`}</span>
      <span className="text-xs text-fg-muted">{g.platform} · <bdi>{g.min_version_code === g.max_version_code ? g.min_version_code : `${g.min_version_code}–${g.max_version_code}`}</bdi> · {t`last ${when}`}</span>
    </div>
  );
}

function GroupDetail({ id, query, n }: { id: string; query: { platform?: string; days: number; version?: number }; n: (v: number) => string }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const q = useQuery({
    queryKey: ['app-crash', tenant.slug, id, query.days, query.version],
    queryFn: async () => (await api.GET('/v1/apps/crashes/{id}', { params: { path: { id }, query: { days: query.days, version: query.version } } })).data!,
  });
  if (q.isPending) return <div className="p-3"><Skeleton className="h-32" /></div>;
  const d = q.data;
  if (!d) return null;
  return (
    <div className="space-y-3 border-t border-line bg-surface/50 p-3">
      <table className="w-full text-xs">
        <thead className="text-fg-muted"><tr><th className="text-start">{t`Version code`}</th><th className="text-start">{t`Device`}</th><th className="text-start">{t`API`}</th><th className="text-start">{t`Sessions`}</th><th className="text-start">{t`Occurrences`}</th></tr></thead>
        <tbody>
          {d.breakdown.map((b, i) => (
            <tr key={i} className="border-t border-line"><td><bdi>{b.app_version_code}</bdi></td><td dir="ltr" className="text-start">{`${b.manufacturer} ${b.model}`.trim() || '—'}</td><td><bdi>{b.api || '—'}</bdi></td><td><bdi>{n(b.reports)}</bdi></td><td><bdi>{n(b.occurrences)}</bdi></td></tr>
          ))}
        </tbody>
      </table>
      <div>
        <h3 className="mb-1 text-xs font-semibold text-fg-muted">{t`Newest stack (scrubbed)`}</h3>
        <pre className="max-h-96 overflow-auto rounded-md bg-surface p-2 text-xs" dir="ltr">{d.stack || t`No stack was sent.`}</pre>
      </div>
    </div>
  );
}
